import React, {
  PropsWithChildren,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from "react";
import { DragButton } from "./DragButton";
import { DrawerCloseButton } from "./DrawerCloseButton";
import { useResizables } from "./hooks/use-resizables";
import { DrawerMask, DrawerWrapper, DrawerContent } from "./atom";
import { DrawerPlacementType } from "../../constants";
import { SimpleInterpolation } from "styled-components";

export type Placement = "left" | "top" | "right" | "bottom";

export type TaskEvaDrawerProps = {
  children?: ReactNode;
  open?: boolean; // 是否显示
  mask?: boolean; // 是否显示蒙层
  placement?: DrawerPlacementType; // 抽屉方向
  width?: number; // 固定宽度
  minWidth?: [number]; // 多层拖拽最小宽度
  draggable?: boolean; // 是否可拖拽
  render?: React.ReactNode; // 自定义内容
  closeIcon?: boolean; // 显示关闭icon
  closeOnClickMask?: boolean; // 点击蒙层关闭
  customCSS?: SimpleInterpolation; // 自定义样式
  zIndex?: number;
  notTriggerOverflow?: boolean;
  onClose?: (e?: React.MouseEvent) => void;
};

export const Drawer: React.FC<TaskEvaDrawerProps> = ({
  children,
  open,
  closeIcon = true,
  closeOnClickMask = true,
  width = 400,
  minWidth = [],
  mask = true,
  placement = "right" as DrawerPlacementType,
  draggable = false,
  customCSS,
  render,
  zIndex = 1050,
  notTriggerOverflow = false,
  onClose = () => {},
}) => {
  const [drawerWidth, setDrawerWidth] = useState(width);
  const drawerContentRef = useRef<HTMLDivElement>(null);
  const { x, dragHandler, startWidth } = useResizables({
    x: 0,
    y: 0,
    ref: drawerContentRef.current,
  });

  function findRangeIndex(value: number, array: Array<number>): number {
    for (let i = 0; i < array.length; i++) {
      if (value < array[i]) {
        return i;
      }
    }
    return -1;
  }

  useEffect(() => {
    const dragWidth = Number(x) + startWidth;
    if (minWidth.length > 0) {
      const rangeIndex = findRangeIndex(dragWidth, minWidth);
      if (rangeIndex !== -1) {
        setDrawerWidth(minWidth[rangeIndex]);
      } else {
        setDrawerWidth(dragWidth);
      }
    } else {
      setDrawerWidth(dragWidth);
    }
  }, [x]);

  useEffect(() => {
    if (notTriggerOverflow || open) {
      document.body.style.overflow = "hidden";
    }
    return () => {
      if (notTriggerOverflow) {
        document.body.style.overflow = "hidden";
      } else {
        document.body.style.overflow = "auto";
      }
    };
  }, [open, notTriggerOverflow]);

  useEffect(() => {
    setDrawerWidth(width);
  }, [width]);

  if (!open) return null;
  return (
    <>
      <DrawerWrapper
        customCSS={customCSS}
        width={drawerWidth}
        $mask={mask}
        zIndex={zIndex}
      >
        {mask && (
          <DrawerMask
            onClick={() => closeOnClickMask && onClose()}
          ></DrawerMask>
        )}
        <DrawerContent
          placement={placement}
          width={drawerWidth}
          ref={drawerContentRef}
          className="drawer-content"
        >
          {closeIcon && (
            <DrawerCloseButton onClose={onClose}></DrawerCloseButton>
          )}
          {draggable && (
            <DragButton
              placement={placement}
              dragHandler={dragHandler}
            ></DragButton>
          )}
          {render ? render : children}
        </DrawerContent>
      </DrawerWrapper>
    </>
  );
};
